// Reports — administrative report hub.
//
// Two-level navigation:
//   1. Module pills at the top (Outpatient · Billing & Accounts · …).
//      Selecting a module changes which list of reports is visible.
//   2. Report cards in a grid for the selected module. Clicking a card
//      mounts ReportViewer with that report's config — filters,
//      summary cards, optional chart, table, and four export buttons
//      (Excel / PDF / Word / CSV).
//
// Adding a new report:
//   - Append an entry to the matching module's `reports` array below.
//   - Each report is a config object understood by ReportViewer.
// Adding a new module:
//   - Append an entry to MODULES below with its own reports array.

import { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Avatar, Button, Chip,
  Grid, IconButton, alpha, Divider,
} from '@mui/material';
import {
  AssessmentOutlined,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  ArrowBackIosNew as BackIcon,
  Schedule as ClockIcon,
  ListAlt as ListIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import ReportViewer, { fmtMoney, fmtNumber } from './ReportViewer';
import {
  reportOutpatientVisits,
  reportOutpatientHourly,
  reportBillingDayIncome,
  reportBillingPaymentMethods,
  reportBillingDetailedTxns,
  reportBillingTrend,
} from '../BaseURL';

const ACCENT = '#1a6b8a';
const todayIso = () => new Date().toISOString().slice(0, 10);

// ─── Module definitions ──────────────────────────────────────────────
const MODULES = [
  {
    id: 'outpatient',
    label: 'Outpatient',
    icon: <PersonIcon />,
    color: '#0e926a',
    description: 'Patient visit analytics — counts, demographics, and trends.',
    reports: [
      {
        id:       'outpatient-visits',
        title:    'Daily Visit Summary',
        subtitle: 'Outpatient registrations per day, broken down by gender and age band.',
        icon:     <PersonIcon fontSize="small" />,
        accent:   '#0e926a',
        fileBase: 'outpatient-visits',
        filterFields: [
          { name: 'start', label: 'From', type: 'date' },
          { name: 'end',   label: 'To',   type: 'date' },
        ],
        defaultFilters: () => {
          const today = todayIso();
          const d = new Date(); d.setDate(d.getDate() - 6);
          return { start: d.toISOString().slice(0, 10), end: today };
        },
        fetch:   (f) => reportOutpatientVisits(f),
        summaryCards: (s) => [
          { label: 'Total visits', value: fmtNumber(s.total),     color: '#0e926a' },
          { label: 'Male',         value: fmtNumber(s.male) },
          { label: 'Female',       value: fmtNumber(s.female) },
          { label: 'Adult',        value: fmtNumber(s.adult) },
          { label: 'Pediatric',    value: fmtNumber(s.pediatric) },
        ],
        chart: (rows) => rows.map((r) => ({
          label: String(r.visit_date || '').slice(5), // MM-DD
          value: r.total,
        })),
        columns: [
          { key: 'visit_date', label: 'Date' },
          { key: 'total',      label: 'Total',     align: 'right', value: (r) => fmtNumber(r.total) },
          { key: 'male',       label: 'Male',      align: 'right', value: (r) => fmtNumber(r.male) },
          { key: 'female',     label: 'Female',    align: 'right', value: (r) => fmtNumber(r.female) },
          { key: 'adult',      label: 'Adult',     align: 'right', value: (r) => fmtNumber(r.adult) },
          { key: 'pediatric',  label: 'Pediatric', align: 'right', value: (r) => fmtNumber(r.pediatric) },
        ],
      },
      {
        id:       'outpatient-hourly',
        title:    'Hourly Visit Trend',
        subtitle: 'When patients walked in across the selected day.',
        icon:     <ClockIcon fontSize="small" />,
        accent:   '#0e926a',
        fileBase: 'outpatient-hourly',
        filterFields: [
          { name: 'date', label: 'Date', type: 'date' },
        ],
        defaultFilters: () => ({ date: todayIso() }),
        fetch:   (f) => reportOutpatientHourly(f),
        summaryCards: (s) => [
          { label: 'Total visits',    value: fmtNumber(s.total),    color: '#0e926a' },
          { label: 'Peak hour',       value: s.peakHour || '—' },
          { label: 'Peak hour count', value: fmtNumber(s.peakCount) },
        ],
        chart: (rows) => rows.map((r) => ({ label: r.label, value: r.total })),
        columns: [
          { key: 'label',  label: 'Hour' },
          { key: 'total',  label: 'Total',  align: 'right', value: (r) => fmtNumber(r.total) },
          { key: 'male',   label: 'Male',   align: 'right', value: (r) => fmtNumber(r.male) },
          { key: 'female', label: 'Female', align: 'right', value: (r) => fmtNumber(r.female) },
        ],
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing & Accounts',
    icon: <WalletIcon />,
    color: '#b45309',
    description: 'Financial reports — income by category, payment methods, daily totals.',
    reports: [
      {
        id:       'billing-day-income',
        title:    'Day Income Summary',
        subtitle: 'Total income per category over the chosen date range.',
        icon:     <MoneyIcon fontSize="small" />,
        accent:   '#b45309',
        fileBase: 'day-income',
        filterFields: [
          { name: 'start', label: 'From', type: 'date' },
          { name: 'end',   label: 'To',   type: 'date' },
        ],
        defaultFilters: () => {
          const today = todayIso();
          const d = new Date(); d.setDate(d.getDate() - 6);
          return { start: d.toISOString().slice(0, 10), end: today };
        },
        fetch: (f) => reportBillingDayIncome(f),
        summaryCards: (s) => [
          { label: 'Total income',  value: fmtMoney(s.total),  color: '#b45309' },
          { label: 'Cash',          value: fmtMoney(s.cash) },
          { label: 'Credit',        value: fmtMoney(s.credit) },
          { label: 'Other',         value: fmtMoney(s.other) },
          { label: 'Transactions',  value: fmtNumber(s.transactions) },
        ],
        chart: (rows) => rows.slice(0, 12).map((r) => ({
          label: String(r.category || '').slice(0, 6),
          value: r.total,
        })),
        columns: [
          { key: 'category',     label: 'Category' },
          { key: 'transactions', label: 'Txns',   align: 'right', value: (r) => fmtNumber(r.transactions) },
          { key: 'total',        label: 'Total',  align: 'right', value: (r) => fmtMoney(r.total) },
          { key: 'cash',         label: 'Cash',   align: 'right', value: (r) => fmtMoney(r.cash) },
          { key: 'credit',       label: 'Credit', align: 'right', value: (r) => fmtMoney(r.credit) },
          { key: 'other',        label: 'Other',  align: 'right', value: (r) => fmtMoney(r.other) },
        ],
      },
      {
        id:       'billing-payment-methods',
        title:    'Payment Methods Breakdown',
        subtitle: 'Income grouped by payment mode (Cash / Credit / Insurance / …).',
        icon:     <ReceiptIcon fontSize="small" />,
        accent:   '#b45309',
        fileBase: 'payment-methods',
        filterFields: [
          { name: 'start', label: 'From', type: 'date' },
          { name: 'end',   label: 'To',   type: 'date' },
        ],
        defaultFilters: () => {
          const today = todayIso();
          const d = new Date(); d.setDate(d.getDate() - 6);
          return { start: d.toISOString().slice(0, 10), end: today };
        },
        fetch: (f) => reportBillingPaymentMethods(f),
        summaryCards: (s) => [
          { label: 'Total income', value: fmtMoney(s.total), color: '#b45309' },
          { label: 'Transactions', value: fmtNumber(s.transactions) },
        ],
        chart: (rows) => rows.map((r) => ({ label: r.method, value: r.total })),
        columns: [
          { key: 'method',       label: 'Method' },
          { key: 'transactions', label: 'Txns',     align: 'right', value: (r) => fmtNumber(r.transactions) },
          { key: 'total',        label: 'Total',    align: 'right', value: (r) => fmtMoney(r.total) },
          { key: 'avg_amount',   label: 'Avg / Txn', align: 'right', value: (r) => fmtMoney(r.avg_amount) },
        ],
      },
      {
        id:       'billing-detailed',
        title:    'Detailed Transactions',
        subtitle: 'Row-per-payment with patient, item, doctor, mode and status. Reversed rows are struck through. Capped at 1,000 rows — narrow the date range to see specific days.',
        icon:     <ListIcon fontSize="small" />,
        accent:   '#b45309',
        fileBase: 'detailed-transactions',
        filterFields: [
          { name: 'start',  label: 'From',   type: 'date' },
          { name: 'end',    label: 'To',     type: 'date' },
          { name: 'status', label: 'Status (all/paid/pending/credit)', type: 'text' },
          { name: 'source', label: 'Source (all/service/lab/medication/other)', type: 'text' },
        ],
        // Default to TODAY ONLY for the heaviest report. Cashier rooms
        // care about today's activity; older runs are an explicit
        // filter change. Keeps first-paint snappy and the row cap
        // generous.
        defaultFilters: () => {
          const today = todayIso();
          return { start: today, end: today, status: 'all', source: 'all' };
        },
        fetch: (f) => reportBillingDetailedTxns(f),
        // Hook for the strike-through row treatment. The backend marks
        // is_reversed=1 when day_income has a 'reversal' entry for the
        // same trans_code.
        isReversed: (r) => !!Number(r.is_reversed),
        summaryCards: (s) => [
          { label: 'Total amount',  value: fmtMoney(s.total),  color: '#b45309' },
          { label: 'Transactions',  value: fmtNumber(s.transactions) },
          { label: 'Reversed',      value: fmtNumber(s.reversed) },
          { label: 'Pending',       value: fmtNumber(s.pending) },
        ],
        columns: [
          { key: 'date',         label: 'Date' },
          { key: 'patient_id',   label: 'Patient ID' },
          { key: 'patient_name', label: 'Patient', value: (r) => (r.patient_name || '').trim() || '—' },
          { key: 'phone',        label: 'Phone',   value: (r) => r.phone || '—' },
          { key: 'gender',       label: 'Sex',     value: (r) => r.gender || '—' },
          { key: 'age',          label: 'Age',     align: 'right', value: (r) => r.age != null ? r.age : '—' },
          { key: 'source',       label: 'Source' },
          { key: 'item',         label: 'Item' },
          { key: 'amount',       label: 'Amount',  align: 'right', value: (r) => fmtMoney(r.amount) },
          { key: 'payment_mode', label: 'Mode',    value: (r) => r.payment_mode || '—' },
          { key: 'doctor',       label: 'Doctor',  value: (r) => r.doctor || '—' },
          { key: 'status',       label: 'Status',  value: (r) => (r.status || '').replace(/^./, (c) => c.toUpperCase()) },
        ],
      },
      {
        id:       'billing-trend',
        title:    'Income Trend Analysis',
        subtitle: 'Multi-chart view: daily totals over the period, cash vs credit trend, and top categories.',
        icon:     <InsightsIcon fontSize="small" />,
        accent:   '#b45309',
        fileBase: 'income-trend',
        filterFields: [
          { name: 'start', label: 'From', type: 'date' },
          { name: 'end',   label: 'To',   type: 'date' },
        ],
        defaultFilters: () => {
          const today = todayIso();
          const d = new Date(); d.setDate(d.getDate() - 29);   // 30-day window by default
          return { start: d.toISOString().slice(0, 10), end: today };
        },
        fetch: (f) => reportBillingTrend(f),
        summaryCards: (s) => [
          { label: 'Total income',  value: fmtMoney(s.total),       color: '#b45309' },
          { label: 'Days covered',  value: fmtNumber(s.days) },
          { label: 'Avg per day',   value: fmtMoney(s.avgPerDay) },
          { label: 'Peak day',      value: s.peakDate || '—',       sub: fmtMoney(s.peakAmount) },
          { label: 'Cash share',    value: fmtMoney(s.cash) },
          { label: 'Credit share',  value: fmtMoney(s.credit) },
        ],
        // Multi-chart layout. Each chart is its own card; the daily
        // total uses a LINE chart for trend-shape, cash-vs-credit uses
        // BARS for direct comparison, and top-category uses bars too.
        charts: (rows, summary) => {
          // The backend returns `categories` outside the standard rows
          // shape. Stash it on summary via the trend endpoint so we can
          // access it here.
          const cats = Array.isArray(summary?.categories) ? summary.categories : [];
          return [
            { title: 'Daily total income',     type: 'line', color: '#b45309',
              format: fmtMoney,
              data:  rows.map((r) => ({ label: String(r.date || '').slice(5), value: r.total })) },
            { title: 'Cash flow per day',      type: 'bar',  color: '#0e926a',
              format: fmtMoney,
              data:  rows.map((r) => ({ label: String(r.date || '').slice(5), value: r.cash })) },
            { title: 'Credit/Insurance per day', type: 'bar', color: '#5468FF',
              format: fmtMoney,
              data:  rows.map((r) => ({ label: String(r.date || '').slice(5), value: r.credit })) },
            ...(cats.length > 0 ? [{
              title: 'Top income categories',  type: 'bar',  color: '#7c3aed',
              format: fmtMoney,
              data:  cats.map((c) => ({ label: c.category, value: c.total })),
            }] : []),
          ];
        },
        // Small table below the charts — day-by-day breakdown.
        columns: [
          { key: 'date',         label: 'Date' },
          { key: 'total',        label: 'Total',  align: 'right', value: (r) => fmtMoney(r.total) },
          { key: 'cash',         label: 'Cash',   align: 'right', value: (r) => fmtMoney(r.cash) },
          { key: 'credit',       label: 'Credit', align: 'right', value: (r) => fmtMoney(r.credit) },
          { key: 'other',        label: 'Other',  align: 'right', value: (r) => fmtMoney(r.other) },
          { key: 'transactions', label: 'Txns',   align: 'right', value: (r) => fmtNumber(r.transactions) },
        ],
      },
    ],
  },
];

// Resolve defaultFilters lazily — they're functions so dates re-evaluate
// each time a report is opened, otherwise opening a report tomorrow
// would still default to today's filter range.
const resolveDefaultFilters = (report) =>
  typeof report.defaultFilters === 'function' ? report.defaultFilters() : (report.defaultFilters || {});

// ─── Report card ─────────────────────────────────────────────────────
function ReportCard({ report, color, onOpen }) {
  return (
    <Card
      onClick={onOpen}
      sx={{
        cursor: 'pointer',
        border: '1px solid', borderColor: 'divider',
        transition: 'border-color .15s ease, transform .15s ease, box-shadow .15s ease',
        '&:hover': {
          borderColor: alpha(color, 0.5),
          transform: 'translateY(-2px)',
          boxShadow: `0 6px 18px ${alpha(color, 0.18)}`,
        },
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(color, 0.18), color,
            border: `1px solid ${alpha(color, 0.3)}`,
            flexShrink: 0,
          }}>
            {report.icon || <TrendIcon fontSize="small" />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
              {report.title}
            </Typography>
            {report.subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {report.subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────
export default function Reports() {
  const [moduleId, setModuleId] = useState(MODULES[0].id);
  const [openReport, setOpenReport] = useState(null);

  const active = MODULES.find((m) => m.id === moduleId) || MODULES[0];

  // Detail view — one report fills the page.
  if (openReport) {
    // Merge the report config with freshly-resolved default filters so
    // the viewer initialises with today (or this week) on every open.
    const configForViewer = {
      ...openReport,
      defaultFilters: resolveDefaultFilters(openReport),
    };
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
          <IconButton onClick={() => setOpenReport(null)} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
            <BackIcon fontSize="small" />
          </IconButton>
          <Chip
            label={active.label}
            size="small"
            sx={{ bgcolor: alpha(active.color, 0.14), color: active.color, fontWeight: 700 }}
          />
        </Stack>
        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <ReportViewer config={configForViewer} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  // List view — module pills + report grid.
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 3 } }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Avatar sx={{
          bgcolor: alpha(ACCENT, 0.18), color: ACCENT,
          border: `1px solid ${alpha(ACCENT, 0.3)}`,
          width: 44, height: 44,
        }}>
          <AssessmentOutlined />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.15 }}>Reports</Typography>
          <Typography variant="caption" color="text.secondary">
            Pick a module, then a report. Every report has Excel, PDF, Word, and CSV exports.
          </Typography>
        </Box>
      </Stack>

      {/* Module pills */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', useFlexGap: true }}>
        {MODULES.map((m) => {
          const selected = m.id === moduleId;
          return (
            <Button
              key={m.id}
              onClick={() => setModuleId(m.id)}
              startIcon={m.icon}
              variant={selected ? 'contained' : 'outlined'}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 999, px: 2.25,
                bgcolor: selected ? m.color : 'transparent',
                color:   selected ? 'white'  : m.color,
                borderColor: m.color,
                '&:hover': {
                  bgcolor: selected ? m.color : alpha(m.color, 0.08),
                  borderColor: m.color,
                },
              }}
            >
              {m.label}
              <Chip
                size="small"
                label={m.reports.length}
                sx={{
                  ml: 1, height: 20, fontWeight: 700,
                  bgcolor: selected ? 'rgba(255,255,255,0.2)' : alpha(m.color, 0.14),
                  color:   selected ? 'white'                  : m.color,
                }}
              />
            </Button>
          );
        })}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Module description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {active.description}
      </Typography>

      {/* Report grid */}
      {active.reports.length === 0 ? (
        <Card sx={{ border: '1px dashed', borderColor: 'divider' }}>
          <CardContent>
            <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
              <Typography sx={{ fontWeight: 700 }}>No reports yet in this module</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 380 }}>
                Narrate the reports you want here — what each one shows, which filters it needs, and where the data lives — and they'll appear as cards.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={1.5}>
          {active.reports.map((r) => (
            <Grid item xs={12} sm={6} md={4} key={r.id}>
              <ReportCard report={r} color={active.color} onOpen={() => setOpenReport(r)} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
